import { ReactNode } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import {
	AssignStatus,
	InclusionFilterStatus,
	ListSourceType,
	PriorityFilterStatus,
	SignalFilterStatus,
} from "@/enum/list.enum";
import { ROUTE_CONFIG } from "@/routes/config";
import { NoResultsFilterWarning } from "@/components/NoResultsFilterWarning/NoResultsFilterWarning";
import { useGetLeanListQuery } from "@/pages/Private/redux/list/list.api";

import { ListEmptyState } from "../components/ListEmptyState";
import { getListEmptyState, toCount } from "../utils/getListEmptyState";

interface UseListEmptyStateNodeParams {
	/** The list's source type (drives whether "Add a source" is offered). */
	listType?: ListSourceType;
	/** The tab's assign status (so "View ignored" is hidden while already on Ignored). */
	assignStatus?: AssignStatus;
	query?: string;
	filter?: Record<string, unknown>;
	signalFilterStatus: SignalFilterStatus[];
	priorityFilterStatus: PriorityFilterStatus[];
	/** A search query is empty while a filter-modal filter is active (existing warning case). */
	showFilterWarning: boolean;
	onSearchWithoutFilters: () => void;
	setPage: (page: number) => void;
	setInclusionStatus: (status: InclusionFilterStatus[]) => void;
}

const ALL_INCLUSION: InclusionFilterStatus[] = [
	InclusionFilterStatus.ADDED,
	InclusionFilterStatus.UNSURE,
	InclusionFilterStatus.NOT_FOUND,
	InclusionFilterStatus.BLACKLIST,
];

/**
 * Builds the node passed to the list table's `noDataText` slot (PREC-3542): the existing
 * filter warning when a query is filtered out, otherwise one of the designed empty states
 * (or `undefined` to fall back to the table's default message).
 *
 * The list-level `never` / `aged` decision reads the *unscoped* lean list counts (no `assignStatus`)
 * so it is correct on every tab — the tab-scoped query narrows the inclusion counts to the active
 * status and would misclassify (e.g. report "all clean" while disqualified leads remain elsewhere).
 *
 * The `filtered` decision and its breakdown / "Review all N" numbers instead read the *tab-scoped*
 * counts, because "Review all" only flips the inclusion filter within the current tab — so it can
 * only surface disqualified leads that share this tab's assign status. Using the unscoped totals
 * there promised counts (e.g. leads sitting in Ignored) that the action could never reveal.
 */
export const useListEmptyStateNode = ({
	listType,
	assignStatus,
	query,
	filter,
	signalFilterStatus,
	priorityFilterStatus,
	showFilterWarning,
	onSearchWithoutFilters,
	setPage,
	setInclusionStatus,
}: UseListEmptyStateNodeParams): ReactNode => {
	const navigate = useNavigate();
	const { id } = useParams();
	const [searchParams, setSearchParams] = useSearchParams();

	// Unscoped counts (mirrors the Unassigned page's lean query → shared cache, no extra request)
	// drive the list-level `never` / `aged` decision.
	const { data: list } = useGetLeanListQuery({ id: id ? +id : 0, assignStatus: undefined });

	// Tab-scoped counts (mirrors the table's own lean query → shared cache, no extra request) drive
	// the `filtered` decision and its breakdown / "Review all" numbers, which must reflect only the
	// current tab (that is all the "Review all" action can surface).
	const { data: scopedList } = useGetLeanListQuery({ id: id ? +id : 0, assignStatus });

	if (showFilterWarning) {
		return <NoResultsFilterWarning onSearchWithoutFilters={onSearchWithoutFilters} />;
	}

	// PREC-3614: the lead-centric designed empty states (never / filtered / aged) don't apply to
	// Profile Tracker lists — their tracked organizations/profiles live under the Tracked Profiles
	// tab, not as reviewable leads. Fall back to the table's default no-results message instead.
	if (listType === ListSourceType.TRACKER) {
		return undefined;
	}

	const hasActiveFilters =
		!!query ||
		(!!filter && Object.keys(filter).length > 0) ||
		(signalFilterStatus.length > 0 && !signalFilterStatus.includes(SignalFilterStatus.ALL)) ||
		(priorityFilterStatus.length > 0 && !priorityFilterStatus.includes(PriorityFilterStatus.ALL));

	// Disqualified leads still sitting in the CURRENT tab — what "Review all" can actually reveal.
	// `undefined` while the scoped query is still loading so the decision falls back to the unscoped
	// total (avoids a flash of "aged" before the tab-scoped counts arrive).
	const scopedDisqualifiedCount = scopedList
		? toCount(scopedList.excludedCount) +
			toCount(scopedList.maybeCount) +
			toCount(scopedList.blacklistCount)
		: undefined;

	const variant = list
		? getListEmptyState({
				draftCount: list.draftCount,
				savedForLaterCount: list.savedForLaterCount,
				assignedCount: list.assignedCount,
				archivedCount: list.archivedCount,
				doneCount: list.doneCount,
				underReviewCount: list.underReviewCount,
				excludedCount: list.excludedCount,
				maybeCount: list.maybeCount,
				blacklistCount: list.blacklistCount,
				scopedDisqualifiedCount,
				hasActiveFilters,
			})
		: null;

	if (!variant) {
		return undefined;
	}

	// Breakdown chips + "Review all N" read the tab-scoped counts (fall back to the unscoped list
	// while the scoped query loads) so the numbers match what "Review all" reveals in this tab.
	const breakdownSource = scopedList ?? list;

	const canAddSource =
		listType !== ListSourceType.EMPTY && listType !== ListSourceType.LI_CONNECTIONS;
	const showViewIgnored = (list?.archivedCount ?? 0) > 0 && assignStatus !== AssignStatus.ARCHIVED;

	const handleReviewAll = () => {
		setPage(1);
		setInclusionStatus(ALL_INCLUSION);
	};

	const handleViewIgnored = () => {
		setSearchParams(
			{ ...Object.fromEntries(searchParams.entries()), tab: AssignStatus.ARCHIVED },
			{ replace: true }
		);
	};

	return (
		<ListEmptyState
			canAddSource={canAddSource}
			counts={{
				unsure: toCount(breakdownSource?.excludedCount),
				notFound: toCount(breakdownSource?.maybeCount),
				blacklist: toCount(breakdownSource?.blacklistCount),
			}}
			showViewIgnored={showViewIgnored}
			variant={variant}
			onAddSource={() => navigate(`/app/lists/create/source/${id}`)}
			onGoToContacts={() => navigate(ROUTE_CONFIG.CONTACTS)}
			onReviewAll={handleReviewAll}
			onViewIgnored={handleViewIgnored}
		/>
	);
};
